import { FC, useCallback, useEffect, useRef } from 'react'
import { Box, BoxProps, colors, styled, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import { EViewMode, IResponsiveSize, IViewModeElementProps } from './view-mode.types'

export const defaultSize: IResponsiveSize = { xs: 12, sm: 6, md: 4, lg: 3 }

export const defaultViewMode = EViewMode.List

const viewModes = [
  { value: EViewMode.Module, icon: <ViewModuleIcon />, label: 'Card Grid' },
  { value: EViewMode.List, icon: <ViewListIcon />, label: 'Detailed List' }
]

export interface IViewModeProps {
  value?: EViewMode
  onChange?: (value: EViewMode) => void
}

export const ViewModeButtons: FC<IViewModeProps> = (props) => {
  const { value, onChange = () => {} } = props
  const normalizedValue = value && Object.values(EViewMode).includes(value as EViewMode) ? value : defaultViewMode

  const changeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const handleChangeDebound = useCallback(
    (newValue: EViewMode | null) => {
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current)
      }
      changeTimeoutRef.current = setTimeout(() => {
        if (newValue !== null && newValue !== normalizedValue) {
          onChange(newValue)
        }
      }, 250)
    },
    [normalizedValue, onChange]
  )

  return (
    <ToggleButtonGroupCustom size='small' value={normalizedValue} exclusive onChange={(_, v) => handleChangeDebound(v)}>
      {viewModes.map((mode) => (
        <Tooltip key={mode.value} title={mode.label} arrow>
          <ToggleButton size='small' value={mode.value} aria-label={mode.label} sx={{ p: '5px' }}>
            {mode.icon}
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroupCustom>
  )
}

export const createViewModeButtons = () => ViewModeButtons

const ToggleButtonGroupCustom = styled(ToggleButtonGroup)(({ theme }) => ({
  flex: '0 0 auto',
  '.MuiToggleButtonGroup-grouped': {
    '&.Mui-selected': {
      color: colors.common.white,
      backgroundColor: `var(--color-orange, ${colors.blue[600]})`,
      borderColor: `var(--color-orange, ${colors.blue[600]})`,
      '&.MuiToggleButtonGroup-lastButton': {
        borderColor: `var(--color-orange, ${colors.blue[600]})`
      },
      '&:hover': {
        backgroundColor: `var(--color-orange, ${colors.blue[600]})`
      }
    }
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}))

export const LoadingOverlay: FC<BoxProps> = (props) => (
  <LoadingOverlayWrap {...props}>
    <Box className='loading-overlay__content'>
      <Box className='loading-overlay__spinner' />
      <Typography variant='body2' color='primary'>
        Loading...
      </Typography>
    </Box>
  </LoadingOverlayWrap>
)

const LoadingOverlayWrap = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  bgcolor: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: 1000,
  paddingTop: '20vh',
  backdropFilter: 'blur(2px)',
  '.loading-overlay__content': {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  '.loading-overlay__spinner': {
    width: 24,
    height: 24,
    border: '3px solid',
    borderColor: colors.grey[300],
    borderTopColor: `var(--color-orange, ${colors.blue[600]})`,
    borderRadius: '50%',
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' }
    },
    animation: 'spin 1s linear infinite'
  },
  '.MuiTypography-root': {
    color: `var(--color-orange, ${colors.blue[600]})`
  }
})

export const LoadingError: FC<{ error: string }> = ({ error }) => (
  <LoadingErrorWrap>
    <Typography variant='h6' gutterBottom>
      An error occurred
    </Typography>
    <Typography variant='body2'>{error}</Typography>
  </LoadingErrorWrap>
)

const LoadingErrorWrap = styled(Box)({
  marginTop: 3,
  padding: 3,
  border: '1px solid',
  borderColor: 'error.main',
  borderRadius: 1,
  bgcolor: 'error.light',
  color: 'error.contrastText'
})

const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  '& .no-rows-primary': { fill: '#3D4751', ...theme.applyStyles('light', { fill: '#AEB8C2' }) },
  '& .no-rows-secondary': { fill: '#1D2126', ...theme.applyStyles('light', { fill: '#E8EAED' }) }
}))

export const LoadingNoData: FC<IViewModeElementProps> = () => (
  <LoadingNoDataWrap>
    <StyledGridOverlay>
      <svg xmlns='http://www.w3.org/2000/svg' fill='none' width={96} viewBox='0 0 452 257' aria-hidden focusable='false'>
        <path
          className='no-rows-primary'
          d='M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z'
        />
        <path
          className='no-rows-primary'
          d='M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z'
        />
        <path
          className='no-rows-primary'
          d='M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z'
        />
        <path
          className='no-rows-secondary'
          d='M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z'
        />
      </svg>
      <Typography variant='h6' color='text.secondary' gutterBottom>
        No data available
      </Typography>
    </StyledGridOverlay>
    <Typography variant='body2' color='text.secondary'>
      Try changing the filter or search keyword
    </Typography>
  </LoadingNoDataWrap>
)

const LoadingNoDataWrap = styled(Box)({
  marginTop: '8px',
  padding: '24px 12px',
  textAlign: 'center',
  border: '1px dashed',
  borderColor: colors.grey[400],
  borderRadius: '6px'
})

export const LoadingWaiting: FC = () => (
  <LoadingWaitingWrap>
    <Typography variant='h6' color='text.primary' sx={{ fontWeight: 'bold' }}>
      Waiting for data...
    </Typography>
    <Typography variant='body2' color='text.secondary'>
      Please wait while we fetch the data.
    </Typography>
  </LoadingWaitingWrap>
)

const LoadingWaitingWrap = styled(Box)({
  marginTop: '8px',
  padding: '24px 12px',
  textAlign: 'center',
  border: '1px dashed',
  borderColor: colors.grey[400],
  borderRadius: '6px'
})

export const LoadingMissingConfig: React.FC = () => (
  <LoadingMissingConfigWrap>
    <Typography variant='h6' color='text.primary' sx={{ fontWeight: 'bold' }}>
      Missing Configuration
    </Typography>
    <Typography variant='body2' color='text.secondary'>
      We're sorry, but it looks like there's a missing configuration required for this feature to work correctly.
    </Typography>
    <Typography variant='body2' color='text.secondary'>
      Please contact support for assistance.
    </Typography>
  </LoadingMissingConfigWrap>
)

const LoadingMissingConfigWrap = styled(Box)({
  marginTop: 3,
  padding: 4,
  textAlign: 'center',
  border: '1px dashed',
  borderColor: colors.red[700],
  borderRadius: 1
})
